import _ from "lodash";

import env from "../constants/env";
import { getObject, listAllObjects } from "../persistence/s3";
import { getOrgId } from "../actions/orgsActions";
import { getAuthorPropertiesObject } from "./bankRefUtils";

const rawContentCache = {};
const rawContentPromiseCache = {};
const latestSourceKeyCache = {};
const latestSourceKeyPromiseCache = {};

function clone(value) {
	return _.cloneDeep(value);
}

function uniqueNonEmpty(values = []) {
	const seen = {};
	return values.filter((value) => {
		if (!_.isString(value)) {
			return false;
		}
		const trimmed = value.trim();
		if (trimmed === "" || seen[trimmed]) {
			return false;
		}
		seen[trimmed] = true;
		return true;
	});
}

function getBankRefFromEntityOrRef(entityOrRef) {
	if (!entityOrRef || !_.isObject(entityOrRef)) {
		return null;
	}

	const looksLikeDirectRef = (
		_.isObject(_.get(entityOrRef, "ref")) ||
		_.isString(entityOrRef.blockId) ||
		_.isString(entityOrRef.s3Key)
	);

	if (_.isObject(entityOrRef.bankRef) || looksLikeDirectRef) {
		return entityOrRef;
	}

	const authorProps = getAuthorPropertiesObject(entityOrRef);
	const bankRef = _.get(authorProps, "bankRef");
	return _.isObject(bankRef) ? bankRef : null;
}

export function getNormalizedBankRef(entityOrRef, options = {}) {
	const bankRefRaw = getBankRefFromEntityOrRef(entityOrRef);
	if (!_.isObject(bankRefRaw)) {
		return null;
	}

	const ref = _.isObject(bankRefRaw.ref) ? bankRefRaw.ref : {};
	const ownerOrgIdFallback = options.ownerOrgId || getOrgId();
	const rawVersion = !_.isNil(bankRefRaw.version) ? bankRefRaw.version : ref.version;
	const rawVersionPolicy = _.isString(bankRefRaw.versionPolicy)
		? _.trim(bankRefRaw.versionPolicy).toLowerCase()
		: (_.isString(ref.versionPolicy) ? _.trim(ref.versionPolicy).toLowerCase() : "");
	const versionPolicy = rawVersionPolicy === "pinned"
		? "pinned"
		: "latest";
	return {
		...ref,
		...bankRefRaw,
		blockId: bankRefRaw.blockId || ref.blockId || "",
		blockType: bankRefRaw.blockType || ref.blockType || "",
		version: rawVersion,
		versionPolicy,
		scope: bankRefRaw.scope || ref.scope || "org",
		ownerOrgId: bankRefRaw.ownerOrgId || ref.ownerOrgId || ownerOrgIdFallback,
		s3Key: bankRefRaw.s3Key || ref.s3Key || "",
		status: bankRefRaw.status || ref.status || "",
		overrides: _.isObject(bankRefRaw.overrides)
			? bankRefRaw.overrides
			: _.isObject(ref.overrides)
				? ref.overrides
				: {}
	};
}

export function getBankRefSourceKeyCandidates(entityOrRef, options = {}) {
	const normalized = getNormalizedBankRef(entityOrRef, options);
	if (!normalized) {
		return [];
	}

	const isLatestPolicy = normalized.versionPolicy === "latest";
	if (!isLatestPolicy && _.isString(normalized.s3Key) && normalized.s3Key.trim() !== "") {
		return [normalized.s3Key.trim()];
	}

	const hasVersion = !_.isNil(normalized.version) && `${normalized.version}`.trim() !== "";
	if (!_.isString(normalized.blockId) || normalized.blockId.trim() === "") {
		return [];
	}

	const owner = normalized.ownerOrgId || getOrgId();
	const blockId = normalized.blockId.trim();
	const version = hasVersion ? `${normalized.version}`.trim() : "";
	const scope = normalized.scope || "org";

	if (isLatestPolicy) {
		if (scope === "public") {
			return uniqueNonEmpty([
				`public/bankBlocks/${owner}/${blockId}/versions/latest.json`,
				`public/bankBlocks/${blockId}/versions/latest.json`
			]);
		}
		return uniqueNonEmpty([
			`${owner}/bankBlocks/${blockId}/versions/latest.json`
		]);
	}

	if (!hasVersion) {
		return [];
	}

	if (scope === "public") {
		return uniqueNonEmpty([
			`public/bankBlocks/${owner}/${blockId}/versions/${version}.json`,
			`public/bankBlocks/${blockId}/versions/${version}.json`
		]);
	}

	return uniqueNonEmpty([
		`${owner}/bankBlocks/${blockId}/versions/${version}.json`
	]);
}

function extractVersionFromSourceKey(sourceKey = "") {
	const match = sourceKey.match(/\/versions\/([^/]+)\.json$/i);
	return match && match[1] ? match[1] : "";
}

function compareVersionValues(a = "", b = "") {
	const aNum = Number(a);
	const bNum = Number(b);
	const aNumeric = !Number.isNaN(aNum);
	const bNumeric = !Number.isNaN(bNum);
	if (aNumeric && bNumeric) {
		return aNum - bNum;
	}
	return `${a}`.localeCompare(`${b}`, undefined, { numeric: true, sensitivity: "base" });
}

function buildLatestLookupPrefixes(normalized = {}) {
	const owner = normalized.ownerOrgId || getOrgId();
	const blockId = _.isString(normalized.blockId) ? normalized.blockId.trim() : "";
	const scope = normalized.scope || "org";
	if (blockId === "") {
		return [];
	}
	if (scope === "public") {
		return uniqueNonEmpty([
			`public/bankBlocks/${owner}/${blockId}/versions/`,
			`public/bankBlocks/${blockId}/versions/`
		]);
	}
	return uniqueNonEmpty([
		`${owner}/bankBlocks/${blockId}/versions/`
	]);
}

function buildLatestLookupCacheKey(normalized = {}) {
	const owner = normalized.ownerOrgId || getOrgId();
	const blockId = _.isString(normalized.blockId) ? normalized.blockId.trim() : "";
	const scope = normalized.scope || "org";
	return `${scope}|${owner}|${blockId}`;
}

async function findLatestVersionSourceKey(normalized = {}, options = {}) {
	const bypassCache = options.bypassCache === true;
	const cacheKey = buildLatestLookupCacheKey(normalized);
	if (!bypassCache && cacheKey && Object.prototype.hasOwnProperty.call(latestSourceKeyCache, cacheKey)) {
		return latestSourceKeyCache[cacheKey];
	}

	if (!bypassCache && cacheKey && latestSourceKeyPromiseCache[cacheKey]) {
		return latestSourceKeyPromiseCache[cacheKey];
	}

	const runLookup = async () => {
		const prefixes = buildLatestLookupPrefixes(normalized);
		for (let i = 0; i < prefixes.length; i += 1) {
			const prefix = prefixes[i];
			let objects = [];
			try {
				objects = await listAllObjects(env.s3.contentBucket, prefix);
			} catch (err) {
				continue;
			}

			const versionKeys = (objects || [])
				.map((obj) => (obj && _.isString(obj.Key) ? obj.Key : ""))
				.filter((key) => key.startsWith(prefix))
				.filter((key) => /\/versions\/[^/]+\.json$/i.test(key))
				.filter((key) => extractVersionFromSourceKey(key).toLowerCase() !== "latest");

			if (versionKeys.length === 0) {
				continue;
			}

			const sorted = [...versionKeys].sort((a, b) => {
				const aVersion = extractVersionFromSourceKey(a);
				const bVersion = extractVersionFromSourceKey(b);
				return compareVersionValues(bVersion, aVersion);
			});
			return sorted[0];
		}
		return "";
	};

	if (bypassCache) {
		const sourceKey = await runLookup();
		if (cacheKey) {
			latestSourceKeyCache[cacheKey] = sourceKey;
			delete latestSourceKeyPromiseCache[cacheKey];
		}
		return sourceKey;
	}

	if (cacheKey) {
		latestSourceKeyPromiseCache[cacheKey] = runLookup()
			.then((sourceKey) => {
				latestSourceKeyCache[cacheKey] = sourceKey;
				delete latestSourceKeyPromiseCache[cacheKey];
				return sourceKey;
			})
			.catch((err) => {
				delete latestSourceKeyPromiseCache[cacheKey];
				throw err;
			});
		return latestSourceKeyPromiseCache[cacheKey];
	}

	return runLookup();
}

export function extractBankRefDefinition(content) {
	if (_.isObject(content) && _.isObject(content.definition)) {
		return content.definition;
	}
	return content;
}

export function applyBankRefOverrides(definition, overrides) {
	if (!_.isObject(definition)) {
		return definition;
	}
	if (!_.isObject(overrides) || _.isEmpty(overrides)) {
		return definition;
	}

	const effectiveDefinition = clone(definition);
	const rootOverrides = _.isObject(overrides.self) ? overrides.self : overrides;
	const sanitizedOverrides = _.omit(rootOverrides, ["self", "nodes"]);
	if (_.isEmpty(sanitizedOverrides)) {
		return effectiveDefinition;
	}

	const overrideKeys = Object.keys(sanitizedOverrides);
	const explicitPathSet = {};
	overrideKeys.forEach((overrideKey) => {
		if (!_.isString(overrideKey) || overrideKey.trim() === "") {
			return;
		}
		const path = _.toPath(overrideKey).map((part) => `${part}`);
		explicitPathSet[path.join(".")] = true;
	});

	const getLeafAliasKey = (nodeType = "", leafKey = "") => {
		if (!_.isString(nodeType) || !_.isString(leafKey)) {
			return "";
		}
		if (nodeType.startsWith("item")) {
			if (leafKey === "label") {
				return "label1";
			}
			if (leafKey === "label1") {
				return "label";
			}
			return "";
		}
		if (nodeType === "list" || nodeType === "section") {
			if (leafKey === "label") {
				return "name";
			}
			if (leafKey === "name") {
				return "label";
			}
			return "";
		}
		return "";
	};

	_.forEach(sanitizedOverrides, (overrideValue, overrideKey) => {
		if (!_.isString(overrideKey) || overrideKey.trim() === "") {
			return;
		}

		if (overrideKey.indexOf(".") > -1 || overrideKey.indexOf("[") > -1) {
			_.set(effectiveDefinition, overrideKey, clone(overrideValue));
			return;
		}

		if (_.isPlainObject(overrideValue) && _.isPlainObject(effectiveDefinition[overrideKey])) {
			effectiveDefinition[overrideKey] = _.merge({}, effectiveDefinition[overrideKey], clone(overrideValue));
		} else {
			effectiveDefinition[overrideKey] = clone(overrideValue);
		}

		// Keep authoring/runtime alias fields in sync when overrides target one side.
		// Example: item "label" (authoring) must also update runtime "label1".
		const overridePath = _.toPath(overrideKey).map((part) => `${part}`);
		const leafKey = overridePath.length ? overridePath[overridePath.length - 1] : "";
		const parentPath = overridePath.slice(0, -1);
		const parentNode = parentPath.length ? _.get(effectiveDefinition, parentPath) : effectiveDefinition;
		const nodeType = _.get(parentNode, "type", _.get(effectiveDefinition, "type", ""));
		const aliasLeafKey = getLeafAliasKey(nodeType, leafKey);
		if (!aliasLeafKey) {
			return;
		}
		const aliasPath = [...parentPath, aliasLeafKey];
		if (explicitPathSet[aliasPath.join(".")]) {
			return;
		}
		_.set(effectiveDefinition, aliasPath, clone(overrideValue));
	});

	return effectiveDefinition;
}

function withReferenceMeta(node, normalizedBankRef, sourceKey) {
	if (!_.isObject(node)) {
		return node;
	}
	const withMeta = clone(node);
	withMeta.meta = _.merge({}, withMeta.meta, {
		bank: {
			blockId: normalizedBankRef.blockId || "",
			version: normalizedBankRef.version,
			blockType: normalizedBankRef.blockType || "",
			scope: normalizedBankRef.scope || "org",
			ownerOrgId: normalizedBankRef.ownerOrgId || "",
			sourceKey: sourceKey || ""
		}
	});
	return withMeta;
}

function mergeReferencedNode(hostNode, resolvedNode) {
	if (!_.isObject(resolvedNode)) {
		return clone(hostNode);
	}
	const merged = _.merge({}, hostNode || {}, resolvedNode);
	const hostAuthorProps = getAuthorPropertiesObject(hostNode);
	const resolvedAuthorProps = getAuthorPropertiesObject(merged);
	if (_.isObject(hostAuthorProps.bankRef)) {
		merged.authorProperties = {
			...resolvedAuthorProps,
			bankRef: hostAuthorProps.bankRef
		};
	}
	return merged;
}

async function fetchRawContentBySourceKey(sourceKey, options = {}) {
	const bypassCache = options.bypassCache === true;
	if (bypassCache) {
		const content = await getObject(env.s3.contentBucket, sourceKey);
		rawContentCache[sourceKey] = content;
		delete rawContentPromiseCache[sourceKey];
		return content;
	}

	if (Object.prototype.hasOwnProperty.call(rawContentCache, sourceKey)) {
		return rawContentCache[sourceKey];
	}

	if (!rawContentPromiseCache[sourceKey]) {
		rawContentPromiseCache[sourceKey] = getObject(env.s3.contentBucket, sourceKey)
			.then((content) => {
				rawContentCache[sourceKey] = content;
				return content;
			})
			.catch((err) => {
				delete rawContentPromiseCache[sourceKey];
				throw err;
			});
	}

	return rawContentPromiseCache[sourceKey];
}

async function fetchRawContentFromCandidates(sourceKeyCandidates = [], options = {}) {
	const errors = [];
	for (let i = 0; i < sourceKeyCandidates.length; i += 1) {
		const sourceKey = sourceKeyCandidates[i];
		try {
			const rawContent = await fetchRawContentBySourceKey(sourceKey, options);
			return { sourceKey, rawContent };
		} catch (err) {
			errors.push({
				sourceKey,
				message: err && err.message ? err.message : "Unable to fetch source."
			});
		}
	}

	const combinedMessage = errors.length > 0
		? errors.map((error) => `${error.sourceKey}: ${error.message}`).join(" | ")
		: "No source key candidates were provided.";
	const aggregatedError = new Error(`Unable to resolve bank reference source. ${combinedMessage}`);
	aggregatedError.candidates = sourceKeyCandidates;
	aggregatedError.errors = errors;
	throw aggregatedError;
}

async function resolveNodeTree(node, context) {
	if (!_.isObject(node)) {
		return clone(node);
	}

	const resolvedNode = clone(node);
	const children = _.isArray(resolvedNode.children) ? resolvedNode.children : [];
	if (children.length === 0) {
		return resolvedNode;
	}

	const resolvedChildren = [];
	for (let i = 0; i < children.length; i += 1) {
		const child = children[i];
		const childRef = getNormalizedBankRef(child, { ownerOrgId: context.ownerOrgId });
		if (!childRef) {
			const nestedChild = await resolveNodeTree(child, context);
			resolvedChildren.push(nestedChild);
			continue;
		}

		try {
			const childResolved = await resolveBankRefInternal(childRef, {
				...context,
				depth: context.depth
			});
			const mergedChild = mergeReferencedNode(child, childResolved.effectiveDefinition);
			const childWithMeta = withReferenceMeta(mergedChild, childRef, childResolved.sourceKey);
			const nestedChild = await resolveNodeTree(childWithMeta, {
				...context,
				depth: context.depth
			});
			resolvedChildren.push(nestedChild);
		} catch (err) {
			context.diagnostics.push({
				type: "nested_ref_error",
				nodeType: _.get(child, "type", ""),
				nodeGuid: _.get(child, "guid", ""),
				blockId: childRef.blockId || "",
				message: err && err.message ? err.message : "Unable to resolve nested bank reference."
			});
			const fallbackChild = clone(child);
			fallbackChild.meta = _.merge({}, fallbackChild.meta, {
				bankResolveError: err && err.message ? err.message : "Unable to resolve nested bank reference."
			});
			resolvedChildren.push(fallbackChild);
		}
	}

	resolvedNode.children = resolvedChildren;
	return resolvedNode;
}

async function resolveBankRefInternal(entityOrRef, context = {}) {
	const normalized = getNormalizedBankRef(entityOrRef, { ownerOrgId: context.ownerOrgId });
	if (!normalized) {
		throw new Error("No bank reference found.");
	}

	const maxDepth = _.isNumber(context.maxDepth) ? context.maxDepth : 12;
	const depth = _.isNumber(context.depth) ? context.depth : 0;
	const diagnostics = _.isArray(context.diagnostics) ? context.diagnostics : [];
	const stack = _.isArray(context.stack) ? context.stack : [];

	if (depth > maxDepth) {
		throw new Error(`Maximum bank reference depth (${maxDepth}) exceeded.`);
	}

	const sourceKeyCandidates = getBankRefSourceKeyCandidates(normalized, { ownerOrgId: context.ownerOrgId });
	if (sourceKeyCandidates.length === 0) {
		throw new Error("Missing bank reference source details.");
	}

	let resolvedSourceKey = "";
	let rawContent = null;
	try {
		const result = await fetchRawContentFromCandidates(sourceKeyCandidates, {
			bypassCache: context.bypassCache === true
		});
		resolvedSourceKey = result.sourceKey;
		rawContent = result.rawContent;
	} catch (primaryErr) {
		if (normalized.versionPolicy !== "latest") {
			throw primaryErr;
		}
		const latestVersionSourceKey = await findLatestVersionSourceKey(normalized, {
			bypassCache: context.bypassCache === true
		});
		if (!latestVersionSourceKey) {
			throw primaryErr;
		}
		const fallbackCandidates = uniqueNonEmpty([latestVersionSourceKey, ...sourceKeyCandidates]);
		const fallbackResult = await fetchRawContentFromCandidates(fallbackCandidates, {
			bypassCache: context.bypassCache === true
		});
		resolvedSourceKey = fallbackResult.sourceKey;
		rawContent = fallbackResult.rawContent;
	}

	const sourceKey = resolvedSourceKey;
	if (stack.indexOf(sourceKey) > -1) {
		const cycle = [...stack, sourceKey].join(" -> ");
		throw new Error(`Bank reference cycle detected: ${cycle}`);
	}

	const resolvedVersionFromContent = !_.isNil(_.get(rawContent, "version"))
		? _.get(rawContent, "version")
		: extractVersionFromSourceKey(sourceKey);
	const normalizedForMeta = _.merge({}, normalized, {
		version: resolvedVersionFromContent || normalized.version
	});
	const definition = extractBankRefDefinition(rawContent);
	const overridden = applyBankRefOverrides(definition, normalized.overrides);
	const withMeta = withReferenceMeta(overridden, normalizedForMeta, sourceKey);
	const resolvedDefinition = await resolveNodeTree(withMeta, {
		...context,
		stack: [...stack, sourceKey],
		depth: depth + 1,
		maxDepth,
		diagnostics
	});

	return {
		normalizedBankRef: normalizedForMeta,
		sourceKey,
		rawContent,
		definition,
		effectiveDefinition: resolvedDefinition,
		diagnostics
	};
}

export async function resolveBankRefContent(entityOrRef, options = {}) {
	const ownerOrgId = options.ownerOrgId || getOrgId();
	const diagnostics = [];
	return resolveBankRefInternal(entityOrRef, {
		ownerOrgId,
		maxDepth: _.isNumber(options.maxDepth) ? options.maxDepth : 12,
		bypassCache: options.bypassCache === true,
		depth: 0,
		stack: [],
		diagnostics
	});
}

export function clearBankRefResolverCache() {
	Object.keys(rawContentCache).forEach((key) => {
		delete rawContentCache[key];
	});
	Object.keys(rawContentPromiseCache).forEach((key) => {
		delete rawContentPromiseCache[key];
	});
	Object.keys(latestSourceKeyCache).forEach((key) => {
		delete latestSourceKeyCache[key];
	});
	Object.keys(latestSourceKeyPromiseCache).forEach((key) => {
		delete latestSourceKeyPromiseCache[key];
	});
}
